import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, TrendingUp, Trash2, X, Printer, Edit } from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAuthStore } from '../../stores/authStore';
import {
  useIncomes,
  useActiveIncomeDepartments,
  useActiveAccounts,
  useUpdateIncome,
  useDeleteIncome,
} from '../../hooks/useQueries';
import { printIncomeReceipt } from '../../lib/printReceipt';
import { IncomeDepartmentsAPI } from '../../lib/sdk/index';
import type { Income, IncomeType } from '../../lib/sdk/index';
import { getErrorMessage } from '../../lib/utils/errorMessage';

// Debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const ManageIncomes: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 50,
    search: '',
    departmentId: '',
    startDate: '',
    endDate: '',
  });

  const debouncedSearch = useDebounce(searchTerm, 500);

  const { data: incomesResponse, isLoading: incomesLoading, refetch: refetchIncomes } = useIncomes(searchParams);
  const { data: departmentsData = [] } = useActiveIncomeDepartments();
  const { data: accountsData = [] } = useActiveAccounts();

  const incomes: Income[] = incomesResponse?.data || (Array.isArray(incomesResponse) ? incomesResponse : []);
  const departments = Array.isArray(departmentsData) ? departmentsData : [];
  const accounts = Array.isArray(accountsData) ? accountsData : [];
  const totalPages = incomesResponse?.pagination?.totalPages || 1;
  const totalIncomes = incomesResponse?.pagination?.total || incomes.length;

  // Delete and reprint state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingIncome, setDeletingIncome] = useState<Income | null>(null);
  const [reprintingId, setReprintingId] = useState<string | null>(null);

  // Edit modal state
  const [editingIncome, setEditingIncome] = useState<Income | null>(null);
  const [editTypes, setEditTypes] = useState<IncomeType[]>([]);
  const [editTypesLoading, setEditTypesLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    incomeTypeId: '',
    amount: '',
    accountId: '',
    notes: '',
  });

  useEffect(() => {
    setSearchParams({
      page: currentPage,
      limit: 50,
      search: debouncedSearch,
      departmentId: departmentFilter,
      startDate,
      endDate,
    });
  }, [currentPage, debouncedSearch, departmentFilter, startDate, endDate]);

  const deleteMutation = useDeleteIncome({
    onSuccess: () => {
      toast.success('Income record deleted successfully');
      setShowDeleteConfirm(false);
      setDeletingIncome(null);
      refetchIncomes();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to delete income'));
      setShowDeleteConfirm(false);
      setDeletingIncome(null);
    },
  });

  const updateMutation = useUpdateIncome({
    onSuccess: async (response: any) => {
      const updated = (response?.data ?? response) as Income;
      toast.success('Income record updated successfully');
      closeEditModal();
      refetchIncomes();
      // Best-effort reprint — never roll back an edit if the printer fails.
      if (updated?._id) {
        try {
          await printIncomeReceipt(
            {
              incomeId: updated.incomeId,
              receiptId: updated.receiptId,
              verificationCode: updated.verificationCode,
              amount: updated.amount,
              departmentName: updated.departmentName,
              incomeTypeName: updated.incomeTypeName,
              accountName: updated.accountName,
              operatorName: updated.operatorName,
            },
            { mandatory: false }
          );
        } catch {
          // ignore — reprint is best-effort
        }
      }
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update income'));
    },
  });

  const canEditIncome = (income: Income): boolean => {
    const threeDaysAgo = new Date();
    threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
    return new Date(income.createdAt) > threeDaysAgo || user?.role === 'SuperAdmin';
  };

  const openEditModal = async (income: Income) => {
    if (!canEditIncome(income)) {
      toast.error('Cannot edit income records older than 3 days');
      return;
    }
    setEditingIncome(income);
    setEditFormData({
      incomeTypeId: income.incomeTypeId,
      amount: income.amount.toString(),
      accountId: income.accountId,
      notes: income.notes || '',
    });
    // Load the income types mapped to this record's department for the dropdown.
    setEditTypes([]);
    setEditTypesLoading(true);
    try {
      const types = await IncomeDepartmentsAPI.getDepartmentTypes(income.departmentId);
      const active = Array.isArray(types) ? types.filter((t: IncomeType) => t.isActive !== false) : [];
      // Ensure the current type is present even if it was later deactivated.
      if (!active.some((t) => t._id === income.incomeTypeId)) {
        active.push({ _id: income.incomeTypeId, name: income.incomeTypeName } as IncomeType);
      }
      setEditTypes(active);
    } catch {
      setEditTypes([{ _id: income.incomeTypeId, name: income.incomeTypeName } as IncomeType]);
    } finally {
      setEditTypesLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditingIncome(null);
    setEditTypes([]);
    setEditFormData({ incomeTypeId: '', amount: '', accountId: '', notes: '' });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingIncome) return;

    const amountNum = parseFloat(editFormData.amount);
    if (!editFormData.incomeTypeId) { toast.error('Income type is required'); return; }
    if (!amountNum || amountNum <= 0) { toast.error('Amount must be greater than 0'); return; }
    if (!editFormData.accountId) { toast.error('Account is required'); return; }

    updateMutation.mutate({
      id: editingIncome._id,
      data: {
        incomeTypeId: editFormData.incomeTypeId,
        amount: amountNum,
        accountId: editFormData.accountId,
        notes: editFormData.notes.trim() || undefined,
      },
    });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleReprint = async (income: Income) => {
    if (reprintingId) return;
    setReprintingId(income._id);
    try {
      await printIncomeReceipt(
        {
          incomeId: income.incomeId,
          receiptId: income.receiptId,
          verificationCode: income.verificationCode,
          amount: income.amount,
          departmentName: income.departmentName,
          incomeTypeName: income.incomeTypeName,
          accountName: income.accountName,
          operatorName: income.operatorName,
        },
        { mandatory: false }
      );
    } catch (err: any) {
      toast.warning(getErrorMessage(err, 'Reprint failed. Please try again.'));
    } finally {
      setReprintingId(null);
    }
  };

  if (incomesLoading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
            <TrendingUp className="h-8 w-8 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Manage Incomes</h1>
            <p className="text-gray-600 dark:text-gray-400">View and manage organizational income records</p>
          </div>
        </div>
        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => navigate('/add-income')}>
          Add Income
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Records</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalIncomes}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Amount (page)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                Rs {incomes.reduce((sum, i) => sum + i.amount, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Page</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {currentPage} / {totalPages}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="form-label">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by receipt, dept, type..."
                className="pl-10"
              />
            </div>
          </div>
          <div>
            <label className="form-label">Department</label>
            <select
              value={departmentFilter}
              onChange={(e) => { setDepartmentFilter(e.target.value); setCurrentPage(1); }}
              className="form-select"
            >
              <option value="">All Departments</option>
              {departments.map((d: any) => (
                <option key={d._id} value={d._id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Start Date</label>
            <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} />
          </div>
          <div>
            <label className="form-label">End Date</label>
            <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
        {(searchTerm || departmentFilter || startDate || endDate) && (
          <div className="mt-3 flex justify-end">
            <Button variant="secondary" size="sm" leftIcon={<X className="h-3 w-3" />} onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="card">
        {incomes.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No income records found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || departmentFilter || startDate || endDate
                ? 'Try adjusting your filters.'
                : 'Start by adding the first income record.'}
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Receipt ID</TH>
                    <TH>Department</TH>
                    <TH>Type</TH>
                    <TH>Amount</TH>
                    <TH>Account</TH>
                    <TH>Operator</TH>
                    <TH>Date</TH>
                    <TH>Actions</TH>
                  </TR>
                </THead>
                <TBody>
                  {incomes.map((income) => (
                    <TR key={income._id}>
                      <TD className="font-mono text-sm">{income.receiptId}</TD>
                      <TD className="font-medium">{income.departmentName}</TD>
                      <TD>
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {income.incomeTypeName}
                        </span>
                      </TD>
                      <TD className="font-semibold text-green-600 dark:text-green-400">
                        Rs {income.amount.toLocaleString()}
                      </TD>
                      <TD className="text-sm text-gray-500">{income.accountName}</TD>
                      <TD className="text-sm">{income.operatorName}</TD>
                      <TD className="text-sm text-gray-500">
                        {new Date(income.createdAt).toLocaleDateString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </TD>
                      <TD>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReprint(income)}
                            disabled={reprintingId === income._id}
                            leftIcon={<Printer className="h-4 w-4" />}
                            className="text-blue-600 hover:text-blue-700"
                            title="Reprint receipt (best-effort)"
                          >
                            {reprintingId === income._id ? 'Printing...' : 'Reprint'}
                          </Button>
                          {canEditIncome(income) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(income)}
                              leftIcon={<Edit className="h-4 w-4" />}
                              className="text-amber-600 hover:text-amber-700"
                              title="Edit income record"
                            >
                              Edit
                            </Button>
                          )}
                          {canEditIncome(income) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setDeletingIncome(income); setShowDeleteConfirm(true); }}
                              leftIcon={<Trash2 className="h-4 w-4" />}
                              className="text-red-600 hover:text-red-700"
                              title={canEditIncome(income) ? 'Delete income record' : 'Cannot delete records older than 3 days'}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableRoot>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Showing page {currentPage} of {totalPages} ({totalIncomes} total)
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {editingIncome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Edit Income {editingIncome.receiptId}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Department: <span className="font-medium">{editingIncome.departmentName}</span>
            </p>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="form-label">Income Type *</label>
                <select
                  value={editFormData.incomeTypeId}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, incomeTypeId: e.target.value }))}
                  required
                  disabled={editTypesLoading}
                  className="form-select"
                >
                  <option value="">{editTypesLoading ? 'Loading types...' : 'Select income type'}</option>
                  {editTypes.map((type) => (
                    <option key={type._id} value={type._id}>{type.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Amount *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">Rs.</span>
                  <Input
                    type="number"
                    value={editFormData.amount}
                    onChange={(e) => setEditFormData((prev) => ({ ...prev, amount: e.target.value }))}
                    min="0"
                    step="1"
                    required
                    className="pl-12"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Account *</label>
                <select
                  value={editFormData.accountId}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, accountId: e.target.value }))}
                  required
                  className="form-select"
                >
                  <option value="">Select account</option>
                  {accounts.map((account: any) => (
                    <option key={account._id} value={account._id}>
                      {account.name} ({account.accountNumber}) — Rs. {account.balance.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Notes (optional)</label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  rows={2}
                  maxLength={500}
                  className="form-select resize-none"
                />
              </div>

              <p className="text-xs text-amber-600 dark:text-amber-400">
                Changing the amount or account will adjust account balances and reprint the receipt.
              </p>

              <div className="flex space-x-2 pt-2">
                <Button type="submit" variant="primary" className="flex-1" disabled={updateMutation.isLoading}>
                  {updateMutation.isLoading ? 'Updating...' : 'Update Income'}
                </Button>
                <Button type="button" variant="secondary" onClick={closeEditModal} className="flex-1" disabled={updateMutation.isLoading}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && deletingIncome && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <Trash2 className="h-6 w-6 text-red-600 mr-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Income Record</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Are you sure you want to delete income record <strong>{deletingIncome.receiptId}</strong>?
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-6">
              This will also reverse the account transaction (credit of Rs {deletingIncome.amount.toLocaleString()}).
            </p>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                onClick={() => { setShowDeleteConfirm(false); setDeletingIncome(null); }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="error"
                onClick={() => deletingIncome && deleteMutation.mutate(deletingIncome._id)}
                disabled={deleteMutation.isLoading}
                className="flex-1"
              >
                {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageIncomes;
